import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchWithAuth } from "@/lib/utils";

export interface McpAgentAccessData {
  enabled: boolean;
  profile_exists: boolean;
  selected_agent_ids: string[];
  available: Array<{ agent_id: string; display_name: string; description: string | null; selected: boolean }>;
}

export function useMcpAgentAccess(getToken: () => Promise<string>) {
  const { t } = useTranslation("connections");
  const [data, setData] = useState<McpAgentAccessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unsupported, setUnsupported] = useState(false);
  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await fetchWithAuth("/api/mcp/agent-access", { headers: {} }, getToken);
      if ([404, 405, 501].includes(response.status)) { setUnsupported(true); return; }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setUnsupported(false); setData(await response.json() as McpAgentAccessData);
    } catch (err: any) { setError(err?.message ?? t("mcpAgentAccess.couldNotLoad")); }
    finally { setLoading(false); }
  }, [getToken, t]);
  const save = useCallback(async (selected_agent_ids: string[]) => {
    setSaving(true); setError(null);
    try {
      const response = await fetchWithAuth("/api/mcp/agent-access", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ selected_agent_ids }) }, getToken);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setData(await response.json() as McpAgentAccessData); return true;
    } catch (err: any) { setError(err?.message ?? t("mcpAgentAccess.saveFailed")); return false; }
    finally { setSaving(false); }
  }, [getToken, t]);
  const reset = useCallback(async () => {
    setSaving(true); setError(null);
    try {
      const response = await fetchWithAuth("/api/mcp/agent-access", { method: "DELETE", headers: {} }, getToken);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setData(await response.json() as McpAgentAccessData); return true;
    } catch (err: any) { setError(err?.message ?? t("mcpAgentAccess.saveFailed")); return false; }
    finally { setSaving(false); }
  }, [getToken, t]);
  useEffect(() => { void refresh(); }, [refresh]);
  return { data, loading, saving, error, unsupported, refresh, save, reset };
}
